// src/pages/DocumentLibraryPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import documentService from '../services/documentService';
import documentCategoryService from '../services/documentCategoryService';
import techSheetService from '../services/techSheetService';
import productService from '../services/productService';
import ingredientService from '../services/ingredientService';
import bolService from '../services/bolService';
import { usePermissions } from '../context/PermissionContext';
import DocumentCategoriesManager from '../components/DocumentCategoriesManager';

// ── Constants ──────────────────────────────────────────────────────────────────

const ALLOWED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png';
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
  });
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate + 'T12:00:00');
  if (exp < today) return 'expired';
  const in60 = new Date(today);
  in60.setDate(in60.getDate() + 60);
  if (exp <= in60) return 'expiring';
  return 'valid';
}

function StatusBadge({ expiryDate }) {
  const status = getExpiryStatus(expiryDate);
  if (status === 'expired')  return <span className="badge bg-danger">Expired</span>;
  if (status === 'expiring') return <span className="badge bg-warning text-dark">Expiring Soon</span>;
  if (status === 'valid')    return <span className="badge bg-success">Valid</span>;
  return <span className="badge bg-secondary">No Expiry</span>;
}

// Days between today and expiryDate — negative if already past.
function daysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate + 'T12:00:00');
  return Math.round((exp - today) / 86400000);
}

// Summary card for the expiry banner — colored left border, hover reveals the
// matching document list so a user doesn't have to filter/scroll to see what's due.
function ExpiryCard({ id, label, color, docs, hovered, onHover, onLeave }) {
  return (
    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
      <div
        className="bg-white"
        style={{
          border: '1px solid #e5e7eb', borderLeftWidth: 4, borderLeftColor: color,
          borderRadius: 6, padding: '12px 16px',
        }}
        onMouseEnter={() => docs.length && onHover(id)}
        onMouseLeave={onLeave}
      >
        <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, color }}>{docs.length}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
      </div>
      {hovered && docs.length > 0 && (
        <div
          className="shadow bg-white"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            border: '1px solid #e5e7eb', borderRadius: 6, zIndex: 20,
            maxHeight: 240, overflowY: 'auto', padding: '6px 0',
          }}
        >
          {docs.map(d => (
            <div key={d.id} className="d-flex justify-content-between gap-2" style={{ fontSize: 12, padding: '4px 12px' }}>
              <span className="text-truncate">{d.name}</span>
              <span className="text-muted text-nowrap">{d.expiryDate}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function linkedLabel(doc) {
  const parts = [];
  if (doc.product)    parts.push(`Product: ${doc.product.name}`);
  if (doc.ingredient) parts.push(`Ingredient: ${doc.ingredient.name}`);
  if (doc.bolId)      parts.push(`BOL #${doc.bolId}`);
  if (doc.lotCode)    parts.push(`Lot: ${doc.lotCode}`);
  return parts.length ? parts.join(' · ') : '—';
}

const EMPTY_FORM = {
  name: '',
  documentCategoryId: '',
  issueDate: '',
  expiryDate: '',
  productId: '',
  ingredientId: '',
  bolId: '',
  lotCode: '',
  notes: '',
};

// ── Confirm Modal ──────────────────────────────────────────────────────────────

function ConfirmModal({ modal, onClose }) {
  if (!modal) return null;
  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title">{modal.title}</h5>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="mb-0">{modal.message}</p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-danger"
              onClick={() => { modal.onConfirm(); onClose(); }}
            >
              {modal.confirmLabel || 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Lot Code Autocomplete ──────────────────────────────────────────────────────

function LotCodeInput({ value, onChange, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [validated, setValidated] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (val) => {
    onChange(val);
    setValidated(false);
    setNotFound(false);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await documentService.searchLotCodes(val);
        setSuggestions(results);
        setOpen(results.length > 0);
        // If user has typed at least 3 chars and no results, mark as not found
        if (val.length >= 3 && results.length === 0) setNotFound(true);
        else setNotFound(false);
      } catch { /* ignore */ }
    }, 300);
  };

  const handleSelect = (lc) => {
    onChange(lc);
    setSuggestions([]);
    setOpen(false);
    setNotFound(false);
    setValidated(true);
  };

  const borderColor = notFound ? '#dc3545' : validated ? '#198754' : undefined;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className={`form-control${notFound ? ' is-invalid' : validated ? ' is-valid' : ''}`}
        style={borderColor ? { borderColor } : {}}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        placeholder="e.g. 2024-001-A"
        autoComplete="off"
      />
      {notFound && (
        <div className="invalid-feedback d-block" style={{ fontSize: 11 }}>
          No matching lot code found — document will still be saved.
        </div>
      )}
      {open && suggestions.length > 0 && (
        <ul className="list-group shadow-sm" style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          zIndex: 1070, maxHeight: 200, overflowY: 'auto', marginTop: 2,
        }}>
          {suggestions.map((lc) => (
            <li
              key={lc}
              className="list-group-item list-group-item-action py-1 px-3"
              style={{ cursor: 'pointer', fontSize: 13 }}
              onMouseDown={() => handleSelect(lc)}
            >
              {lc}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Upload / Edit Modal ────────────────────────────────────────────────────────

function DocumentModal({
  mode, // 'upload' | 'edit'
  initial,
  products,
  ingredients,
  bols,
  categories,
  onClose,
  onSaved,
}) {
  // Active tabs, plus the document's current tab even if it's since been deactivated —
  // editing an existing document should never silently clear its tab.
  const categoryOptions = categories.filter(c =>
    c.isActive || String(c.id) === String(initial?.documentCategoryId)
  );
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (f) => {
    if (!f) return;
    if (!ALLOWED_MIME_TYPES.includes(f.type)) {
      setError('File type not allowed. Accepted: PDF, JPEG, PNG.');
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError('File exceeds the 15 MB size limit.');
      return;
    }
    setError('');
    setFile(f);
    if (!form.name) set('name', f.name.replace(/\.[^.]+$/, ''));
    if (!form.documentCategoryId && f.name.toLowerCase().includes('coa')) {
      const coaCategory = categories.find(c => c.systemKey === 'coa' && c.isActive);
      if (coaCategory) set('documentCategoryId', String(coaCategory.id));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) return setError('Document name is required.');
    if (!form.documentCategoryId) return setError('Tab is required.');

    if (mode === 'upload') {
      if (!file) return setError('Please select a file to upload.');
      try {
        setUploading(true);
        setProgress(10);

        // 1) Get presigned URL
        const { uploadUrl, publicUrl } = await documentService.getUploadUrl(
          file.name, file.type, file.size
        );
        setProgress(30);

        // 2) PUT file directly to S3
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setProgress(30 + Math.round((evt.loaded / evt.total) * 60));
            }
          },
        });
        setProgress(90);

        // 3) Save record
        const payload = {
          name: form.name.trim(),
          documentCategoryId: form.documentCategoryId,
          fileUrl: publicUrl,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          issueDate:    form.issueDate    || null,
          expiryDate:   form.expiryDate   || null,
          productId:    form.productId    || null,
          ingredientId: form.ingredientId || null,
          bolId:        form.bolId        || null,
          lotCode:      form.lotCode      || null,
          notes:        form.notes        || null,
        };
        const result = await documentService.createDocument(payload);
        setProgress(100);
        onSaved(result.document);
      } catch (err) {
        setError(err?.response?.data?.message || 'Upload failed. Please try again.');
        setUploading(false);
        setProgress(0);
      }
    } else {
      // edit mode
      try {
        setUploading(true);
        const payload = {
          name:               form.name.trim(),
          documentCategoryId: form.documentCategoryId,
          issueDate:    form.issueDate    || null,
          expiryDate:   form.expiryDate   || null,
          productId:    form.productId    || null,
          ingredientId: form.ingredientId || null,
          bolId:        form.bolId        || null,
          lotCode:      form.lotCode      || null,
          notes:        form.notes        || null,
        };
        const result = await documentService.updateDocument(initial.id, payload);
        onSaved(result.document);
      } catch (err) {
        setError(err?.response?.data?.message || 'Save failed. Please try again.');
        setUploading(false);
      }
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1055 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {mode === 'upload' ? 'Upload Document' : 'Edit Document'}
            </h5>
            <button className="btn-close" onClick={onClose} disabled={uploading} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              {/* ── File drop zone (upload mode only) ── */}
              {mode === 'upload' && (
                <div
                  className={`border rounded p-4 text-center mb-3 ${dragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed'}`}
                  style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div>
                      <strong>{file.name}</strong>
                      <div className="text-muted small mt-1">{formatBytes(file.size)}</div>
                    </div>
                  ) : (
                    <div className="text-muted">
                      <div style={{ fontSize: 32 }}>📄</div>
                      <div>Drag & drop a file here, or click to browse</div>
                      <div className="small mt-1">PDF, JPEG, PNG — max 15 MB</div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_EXTENSIONS}
                    className="d-none"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              )}

              {/* ── Progress bar ── */}
              {uploading && (
                <div className="progress mb-3" style={{ height: 8 }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold">Document Name <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="e.g. COA – Wheat Flour Lot A123"
                    disabled={uploading}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Tab <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={form.documentCategoryId}
                    onChange={(e) => set('documentCategoryId', e.target.value)}
                    disabled={uploading}
                    required
                  >
                    <option value="">— Select tab —</option>
                    {categoryOptions.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{!c.isActive ? ' (inactive)' : ''}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Issue Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.issueDate}
                    onChange={(e) => set('issueDate', e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Expiry Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.expiryDate}
                    onChange={(e) => set('expiryDate', e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Link to Product</label>
                  <select
                    className="form-select"
                    value={form.productId}
                    onChange={(e) => set('productId', e.target.value)}
                    disabled={uploading}
                  >
                    <option value="">— None —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Link to Ingredient</label>
                  <select
                    className="form-select"
                    value={form.ingredientId}
                    onChange={(e) => set('ingredientId', e.target.value)}
                    disabled={uploading}
                  >
                    <option value="">— None —</option>
                    {ingredients.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Link to BOL</label>
                  <select
                    className="form-select"
                    value={form.bolId}
                    onChange={(e) => set('bolId', e.target.value)}
                    disabled={uploading}
                  >
                    <option value="">— None —</option>
                    {bols.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bolNumber || `BOL #${b.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Link to Lot Code</label>
                  <LotCodeInput
                    value={form.lotCode}
                    onChange={(v) => set('lotCode', v)}
                    disabled={uploading}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    disabled={uploading}
                    placeholder="Optional notes…"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading
                  ? mode === 'upload' ? 'Uploading…' : 'Saving…'
                  : mode === 'upload' ? 'Upload' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function DocumentLibraryPage() {
  const { hasModule } = usePermissions();
  const canUpload = hasModule('documents.upload');
  const canManage = hasModule('documents.manage');

  const [pageView, setPageView] = useState('documents'); // 'documents' | 'techsheets'

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(''); // '' = All tabs
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'valid' | 'expiring' | 'expired' | 'none'
  const [showArchived, setShowArchived] = useState(false);

  // Tabs
  const [categories, setCategories] = useState([]);
  const [addingTab, setAddingTab] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [manageTabsOpen, setManageTabsOpen] = useState(false);

  // Reference data
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [bols, setBols] = useState([]);

  // Tech Sheets
  const [techSheets, setTechSheets] = useState([]);

  // Modals
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);   // Document being edited
  const [confirmModal, setConfirmModal] = useState(null);

  // ── Load tabs ────────────────────────────────────────────────────────────────
  const loadCategories = useCallback(async () => {
    try {
      setCategories(await documentCategoryService.getCategories());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // ── Load reference data ──────────────────────────────────────────────────────
  useEffect(() => {
    productService.getProducts().then((data) => {
      setProducts(Array.isArray(data) ? data : data?.products || []);
    }).catch(() => {});

    ingredientService.getIngredients().then((data) => {
      setIngredients(Array.isArray(data) ? data : data?.ingredients || []);
    }).catch(() => {});

    bolService.getBols && bolService.getBols().then((data) => {
      setBols(Array.isArray(data) ? data : data?.bols || []);
    }).catch(() => {});

    techSheetService.list().then(setTechSheets).catch(() => {});
  }, []);

  // ── Load documents ───────────────────────────────────────────────────────────
  const loadDocs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (categoryId) params.categoryId = categoryId;
      if (search.trim()) params.search = search.trim();
      if (showArchived) {
        params.archived = 'all'; // show all including archived
      }
      if (statusFilter === 'expired')  params.expired = 'true';
      if (statusFilter === 'expiring') params.expiring = 'true';

      const data = await documentService.getDocuments(params);
      let result = data.documents || [];

      // Client-side filter for 'valid' / 'none' (backend doesn't have these filters)
      if (statusFilter === 'valid') {
        result = result.filter((d) => getExpiryStatus(d.expiryDate) === 'valid');
      } else if (statusFilter === 'none') {
        result = result.filter((d) => !d.expiryDate);
      }

      setDocs(result);
    } catch (err) {
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, search, showArchived, statusFilter]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  // ── Expiry summary buckets (disjoint — each doc lands in exactly one) ─────────
  const expiredDocs = docs.filter((d) => !d.isArchived && daysUntilExpiry(d.expiryDate) !== null && daysUntilExpiry(d.expiryDate) < 0);
  const expiring30Docs = docs.filter((d) => {
    if (d.isArchived) return false;
    const n = daysUntilExpiry(d.expiryDate);
    return n !== null && n >= 0 && n <= 30;
  });
  const expiring60Docs = docs.filter((d) => {
    if (d.isArchived) return false;
    const n = daysUntilExpiry(d.expiryDate);
    return n !== null && n > 30 && n <= 60;
  });
  const [hoveredCard, setHoveredCard] = useState(null);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSaved = (doc) => {
    setUploadOpen(false);
    setEditDoc(null);
    loadDocs();
  };

  const handleArchive = async (doc) => {
    try {
      await documentService.archiveDocument(doc.id, !doc.isArchived);
      loadDocs();
    } catch {
      alert('Failed to update archive status.');
    }
  };

  const handleDelete = (doc) => {
    setConfirmModal({
      title: 'Delete Document',
      message: `Are you sure you want to permanently delete "${doc.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await documentService.deleteDocument(doc.id);
          loadDocs();
        } catch {
          alert('Failed to delete document.');
        }
      },
    });
  };

  const createTab = async () => {
    if (!newTabName.trim()) { setAddingTab(false); return; }
    try {
      const maxOrder = categories.reduce((m, c) => Math.max(m, c.sortOrder || 0), 0);
      const created = await documentCategoryService.createCategory({ name: newTabName.trim(), sortOrder: maxOrder + 10 });
      setNewTabName('');
      setAddingTab(false);
      await loadCategories();
      setCategoryId(String(created.id));
    } catch {
      alert('Failed to create tab.');
    }
  };

  const editFormInitial = editDoc ? {
    id:           editDoc.id,
    name:         editDoc.name,
    documentCategoryId: editDoc.documentCategoryId ? String(editDoc.documentCategoryId) : '',
    issueDate:    editDoc.issueDate  || '',
    expiryDate:   editDoc.expiryDate || '',
    productId:    editDoc.productId    ? String(editDoc.productId)    : '',
    ingredientId: editDoc.ingredientId ? String(editDoc.ingredientId) : '',
    bolId:        editDoc.bolId        ? String(editDoc.bolId)        : '',
    lotCode:      editDoc.lotCode      || '',
    notes:        editDoc.notes        || '',
  } : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="mb-0 fw-bold">Documents</h4>
        {canUpload && pageView === 'documents' && (
          <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
            + Upload Document
          </button>
        )}
      </div>

      {/* Section switcher — keeps the (potentially huge) document table and the tech
          sheet list from competing for the same scroll, per user feedback that tech
          sheets used to sit unreachable at the bottom of a long document list. */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${pageView === 'documents' ? 'active' : ''}`}
            onClick={() => setPageView('documents')}
          >
            📄 Documents
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${pageView === 'techsheets' ? 'active' : ''}`}
            onClick={() => setPageView('techsheets')}
          >
            📋 Product Tech Sheets{techSheets.length > 0 ? ` (${techSheets.length})` : ''}
          </button>
        </li>
      </ul>

      {pageView === 'documents' && (
      <>
      {/* Expiry summary cards */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <ExpiryCard
          id="expired" label="Expired" color="#dc3545"
          docs={expiredDocs} hovered={hoveredCard === 'expired'}
          onHover={setHoveredCard} onLeave={() => setHoveredCard(null)}
        />
        <ExpiryCard
          id="expiring30" label="Expiring within 30 days" color="#fd7e14"
          docs={expiring30Docs} hovered={hoveredCard === 'expiring30'}
          onHover={setHoveredCard} onLeave={() => setHoveredCard(null)}
        />
        <ExpiryCard
          id="expiring60" label="Expiring in 31–60 days" color="#ffc107"
          docs={expiring60Docs} hovered={hoveredCard === 'expiring60'}
          onHover={setHoveredCard} onLeave={() => setHoveredCard(null)}
        />
      </div>

      {/* Tab bar */}
      <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
        <button
          className={`btn btn-sm ${!categoryId ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setCategoryId('')}
        >
          All
        </button>
        {categories.filter(c => c.isActive).map(c => (
          <button
            key={c.id}
            className={`btn btn-sm ${categoryId === String(c.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setCategoryId(String(c.id))}
          >
            {c.name}
          </button>
        ))}
        {canManage && (
          addingTab ? (
            <input
              autoFocus
              className="form-control form-control-sm"
              style={{ width: 160 }}
              placeholder="New tab name…"
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createTab(); if (e.key === 'Escape') { setAddingTab(false); setNewTabName(''); } }}
              onBlur={createTab}
            />
          ) : (
            <button className="btn btn-sm btn-outline-secondary" title="New tab" onClick={() => setAddingTab(true)}>+</button>
          )
        )}
        {canManage && (
          <button className="btn btn-sm btn-outline-secondary ms-1" title="Manage tabs" onClick={() => setManageTabsOpen(true)}>⚙</button>
        )}
      </div>

      {/* Filter bar */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 220 }}
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="valid">Valid</option>
          <option value="expiring">Expiring Soon</option>
          <option value="expired">Expired</option>
          <option value="none">No Expiry</option>
        </select>

        <div className="form-check mb-0 ms-1">
          <input
            className="form-check-input"
            type="checkbox"
            id="showArchived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="showArchived">
            Show archived
          </label>
        </div>
      </div>

      {/* Error */}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-muted text-center py-5">No documents found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Tab</th>
                <th>Linked To</th>
                <th>Issue Date</th>
                <th>Expiry</th>
                <th>Status</th>
                <th style={{ width: 160, whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr
                  key={doc.id}
                  className={doc.isArchived ? 'text-muted' : ''}
                  style={doc.isArchived ? { opacity: 0.55 } : {}}
                >
                  <td>
                    <span style={doc.isArchived ? { textDecoration: 'line-through' } : {}}>
                      {doc.name}
                    </span>
                    {doc.isArchived && (
                      <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>Archived</span>
                    )}
                    {(doc.fileSize || doc.category?.name) && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {[formatBytes(doc.fileSize), doc.category?.name].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td>{doc.category?.name || '—'}</td>
                  <td className="small">{linkedLabel(doc)}</td>
                  <td className="small">{fmtDate(doc.issueDate)}</td>
                  <td className="small">{fmtDate(doc.expiryDate)}</td>
                  <td><StatusBadge expiryDate={doc.expiryDate} /></td>
                  <td>
                    <div className="d-flex gap-1 flex-nowrap">
                      {/* Download */}
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        title="Download"
                      >
                        ⬇
                      </a>

                      {/* Edit */}
                      {canManage && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          title="Edit"
                          onClick={() => setEditDoc(doc)}
                        >
                          ✏️
                        </button>
                      )}

                      {/* Archive / Unarchive */}
                      {canManage && (
                        <button
                          className={`btn btn-sm ${doc.isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
                          title={doc.isArchived ? 'Unarchive' : 'Archive'}
                          onClick={() => handleArchive(doc)}
                        >
                          {doc.isArchived ? '↩' : '🗄'}
                        </button>
                      )}

                      {/* Delete */}
                      {canManage && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => handleDelete(doc)}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {/* ── Tech Sheets Section ── */}
      {pageView === 'techsheets' && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="text-muted" style={{ fontSize: 12 }}>Manage tech sheets from the Products page → Tech Sheet tab</span>
          </div>
          {techSheets.length === 0 && (
            <div className="text-muted text-center py-5">No product tech sheets yet.</div>
          )}
          {techSheets.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th>Sheet #</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Approved</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {techSheets.map(s => {
                  const isApproved = s.status === 'approved';
                  return (
                    <tr key={s.id} style={{ opacity: s.status === 'archived' ? 0.6 : 1 }}>
                      <td className="fw-semibold">{s.Product?.name || '—'}</td>
                      <td className="text-muted">{s.sheetNumber || '—'}</td>
                      <td>v{s.version}</td>
                      <td>
                        <span className={`badge text-bg-${isApproved ? 'success' : 'secondary'} text-capitalize`}>{s.status}</span>
                      </td>
                      <td className="text-muted" style={{ fontSize: 12 }}>
                        {s.approvedAt ? new Date(s.approvedAt).toLocaleDateString() : '—'}
                        {s.ApprovedBy && (s.ApprovedBy.firstName || s.ApprovedBy.lastName) && ` — ${s.ApprovedBy.firstName || ''} ${s.ApprovedBy.lastName || ''}`.trimEnd()}
                      </td>
                      <td className="text-end">
                        <a
                          href={`/techsheets/${s.id}/preview`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-secondary btn-sm"
                        >
                          Preview / PDF
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {uploadOpen && (
        <DocumentModal
          mode="upload"
          initial={EMPTY_FORM}
          products={products}
          ingredients={ingredients}
          bols={bols}
          categories={categories}
          onClose={() => setUploadOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {/* Edit Modal */}
      {editDoc && (
        <DocumentModal
          mode="edit"
          initial={editFormInitial}
          products={products}
          ingredients={ingredients}
          bols={bols}
          categories={categories}
          onClose={() => setEditDoc(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />

      {/* Manage Tabs Modal */}
      {manageTabsOpen && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1055 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Manage Tabs</h5>
                <button className="btn-close" onClick={() => setManageTabsOpen(false)} />
              </div>
              <div className="modal-body">
                <DocumentCategoriesManager onChange={() => { loadCategories(); loadDocs(); }} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setManageTabsOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
